import type { BasketOrder, BasketTradingState } from './types';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';

export interface BasketSpecOrder {
    instrument: Instrument;
    quantity?: number;
    side?: string;
    account?: string;
    route?: string;
    exDestination?: string;
    type?: string;
    duration?: string;
    multiplier?: number;
    weight?: number;
    adjustment?: number;
    adjustmentQuantity?: number;
    direction?: 'increase' | 'decrease';
}

export interface BasketSpec {
    id: string;
    name: string;
    basketType: 'quantity' | 'weighted' | 'notional' | 'positionAdjustment';
    account: string;
    side: string;
    route: string;
    exDestination: string;
    type: string;
    quantity: number;
    duration: string;
    direction?: 'increase' | 'decrease';
    multiplier?: number;
    adjustment?: number;
    orders: BasketSpecOrder[];
    weightBasis: 'equal' | 'custom';
    notionalAmount?: number | null;
    notionalType: 'net' | 'gross';
    positionsAccount: string | null;
    firmId?: string | null;
    createdAt?: string;
    updatedAt?: string;
    discardedAt?: string | null;
}

type BaseNewBasketSpec = Omit<
    BasketSpec,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'discardedAt'
    | 'firmId'
    | 'basketType'
    | 'weightBasis'
    | 'notionalAmount'
    | 'notionalType'
    | 'positionsAccount'
>;

interface QuantityNewBasketSpec extends BaseNewBasketSpec {
    basketType: 'quantity';
}

interface WeightedNewBasketSpec extends BaseNewBasketSpec {
    basketType: 'weighted';
    weightBasis: 'equal' | 'custom';
}

interface NotionalNewBasketSpec extends BaseNewBasketSpec {
    basketType: 'notional';
    notionalAmount: number;
    notionalType: 'net' | 'gross';
}

interface PositionAdjustmentNewBasketSpec extends BaseNewBasketSpec {
    basketType: 'positionAdjustment';
    positionsAccount: string;
}

export type NewBasketSpec =
    | QuantityNewBasketSpec
    | WeightedNewBasketSpec
    | NotionalNewBasketSpec
    | PositionAdjustmentNewBasketSpec;

export type BasketSpecPatch = Partial<
    BaseNewBasketSpec & {
        basketType: BasketSpec['basketType'];
        weightBasis: 'equal' | 'custom';
        notionalAmount: number | null;
        notionalType: 'net' | 'gross';
        positionsAccount: string | null;
    }
>;
export type BasketRecord = Pick<BasketSpec, 'id' | 'name'> & { createdAt?: string; updatedAt?: string };

export function basketOrderToSpecOrder(order: BasketOrder): BasketSpecOrder {
    return {
        instrument: order.instrument,
        quantity: order.quantity,
        side: order.side,
        account: order.account,
        route: order.route,
        exDestination: order.exDestination,
        type: order.type,
        duration: order.duration,
        multiplier: order.multiplier,
        weight: order.weight,
        adjustment: order.adjustment,
        adjustmentQuantity: order.adjustmentQuantity,
        direction: order.direction,
    };
}

/**
 * Converts basket state to a spec payload suitable for the API.
 *
 * The backend uses a discriminated union on `basketType`, so each variant must
 * only include the fields relevant to that type. Sending extra fields (e.g.
 * `notionalAmount` on a `quantity` basket) will cause schema validation errors.
 */
export function basketStateToSpec(state: BasketTradingState, name: string): NewBasketSpec {
    // Position adjustment orders are loaded dynamically from positions,
    // so we don't persist them in the spec.
    const orders = state.type === 'positionAdjustment' ? [] : state.basket.map(basketOrderToSpecOrder);

    const base = {
        name,
        account: state.defaults.account ?? '',
        side: state.defaults.side,
        route: state.defaults.route,
        exDestination: state.defaults.exDestination,
        type: state.defaults.type,
        quantity: state.defaults.quantity,
        duration: state.defaults.duration,
        direction: state.defaults.direction,
        multiplier: state.defaults.multiplier,
        adjustment: state.defaults.adjustment,
        orders,
    };

    switch (state.type) {
        case 'quantity':
            return { ...base, basketType: 'quantity' };
        case 'weighted':
            return { ...base, basketType: 'weighted', weightBasis: state.weightBasis };
        case 'notional':
            return {
                ...base,
                basketType: 'notional',
                notionalAmount: state.notionalAmount ?? 0,
                notionalType: state.notionalType,
            };
        case 'positionAdjustment':
            return {
                ...base,
                basketType: 'positionAdjustment',
                positionsAccount: state.positionsAccount ?? '',
            };
    }
}

export function serializableBasketSnapshot(state: BasketTradingState): string {
    const snapshot = {
        type: state.type,
        defaults: state.defaults,
        basket: state.basket.map(basketOrderToSpecOrder),
        weightBasis: state.weightBasis,
        notionalAmount: state.notionalAmount,
        notionalType: state.notionalType,
        positionsAccount: state.positionsAccount ?? null,
    };
    return JSON.stringify(snapshot);
}
